import { isEmpty, isEqual, isNil } from 'lodash';
import { Service, ServicePricing } from 'model/Service';
import { FieldErrorWithKey, ServiceCategoryType } from './model';
import { fixedPriceOption, getPricingVariablesOption } from './Pricing/model';
import { MarketplaceDeposit } from '@spike/marketplace-model';
import { getSimpleDuration, getVariableDuration } from 'utils/ServiceUtils';

export const validateServicePetType = (service: Service): Array<FieldErrorWithKey> => {
    const errors: Array<FieldErrorWithKey> = [];
    if (isEmpty(service.petTypes)) {
        errors.push({
            fieldName: 'petType',
            errorMessage: 'Pet type is required',
            key: ServiceCategoryType.CATEGORY
        });
    }
    return errors;
};

export const validateServiceName = (service: Service): Array<FieldErrorWithKey> => {
    const errors: Array<FieldErrorWithKey> = [];
    if (!service.name || service.name.length === 0) {
        errors.push({
            fieldName: 'name',
            errorMessage: 'Service name is required',
            key: ServiceCategoryType.DETAIL
        });
    }
    return errors;
};

export const validateServiceDuration = (service: Service): Array<FieldErrorWithKey> => {
    const errors: Array<FieldErrorWithKey> = [];

    // Use helpers for safe access to duration properties
    const simple = getSimpleDuration(service);
    const variable = getVariableDuration(service);

    if ((!simple || simple.hours + simple.minutes === 0) && variable.length === 0) {
        errors.push({
            fieldName: 'duration',
            errorMessage: 'Duration is required',
            key: ServiceCategoryType.DETAIL
        });
    }
    if (
        (!simple || simple.hours + simple.minutes === 0) &&
        (variable.length === 0 ||
            variable.some(duration => (duration.duration.hours || 0) + (duration.duration.minutes || 0) === 0))
    ) {
        variable
            .filter(duration => duration.duration.hours + duration.duration.minutes === 0)
            .forEach(duration => {
                errors.push({
                    id: duration.petSize.id,
                    fieldName: 'duration',
                    errorMessage: `Duration is required`,
                    key: ServiceCategoryType.DETAIL
                });
            });
    }

    return errors;
};

export const validateServiceStaff = (service: Service): Array<FieldErrorWithKey> => {
    const errors: Array<FieldErrorWithKey> = [];
    const hasStaff =
        !isEmpty(service.staff) || service.marketplaces.some(marketplace => marketplace.staffIds.length > 0);

    if (!hasStaff) {
        errors.push({
            fieldName: 'staff',
            errorMessage: 'Staff cannot be empty',
            key: ServiceCategoryType.STAFF
        });
    }
    return errors;
};

export const validateService = (service: Service, deposit: MarketplaceDeposit): Array<FieldErrorWithKey> => {
    const petTypeErrors = validateServicePetType(service);
    const nameErrors = validateServiceName(service);
    const durationErrors = validateServiceDuration(service);
    const pricingErrors = validateServicePricingV2(service.pricing, deposit);

    return [...petTypeErrors, ...nameErrors, ...durationErrors, ...pricingErrors];
};

export const validateCategoryStep = (service: Service): boolean => {
    return isEmpty(validateServicePetType(service));
};

export const validateDetailsStep = (service: Service): boolean => {
    return isEmpty([...validateServiceName(service), ...validateServiceDuration(service)]);
};

export const validatePricingStep = (pricing: ServicePricing, deposit: MarketplaceDeposit): boolean => {
    return isEmpty(validateServicePricingV2(pricing, deposit));
};

export const validateStaffStep = (service: Service) => {
    return isEmpty(validateServiceStaff(service));
};

export const validateServiceComplete = (service: Service, deposit: MarketplaceDeposit) => {
    return isEmpty(validateService(service, deposit));
};

export const validateServicePricingV2 = (
    pricing: ServicePricing,
    deposit: MarketplaceDeposit
): Array<FieldErrorWithKey & { petTypeId: string }> => {
    const errors: Array<FieldErrorWithKey & { petTypeId: string; priceUuid: string | null; type: 'inline' | 'alert' }> =
        [];
    const selectedServiceOption = getPricingVariablesOption(pricing.variablePrice?.variables);
    const depositAmount = Number(deposit.value);
    const isPercentageDeposit = deposit.type === 'percentage';

    if (selectedServiceOption.id === fixedPriceOption.id) {
        pricing.fixedPrice?.forEach(price => {
            if (isNil(price.price)) {
                errors.push({
                    fieldName: 'pricing',
                    errorMessage: 'Price is Required',
                    key: ServiceCategoryType.PRICING,
                    petTypeId: price.petType.id,
                    priceUuid: null,
                    type: 'inline'
                });
                return;
            }
            if (!isPercentageDeposit && price.price > 0 && price.price < depositAmount) {
                errors.push({
                    fieldName: 'pricing',
                    errorMessage: '',
                    key: ServiceCategoryType.PRICING,
                    petTypeId: price.petType.id,
                    priceUuid: null,
                    type: 'alert'
                });
            }
        });
    } else {
        pricing.variablePrice?.prices.forEach(price => {
            if (isNil(price.price)) {
                errors.push({
                    fieldName: 'pricing',
                    errorMessage: 'Price is required',
                    key: ServiceCategoryType.PRICING,
                    petTypeId: price.petType!.id,
                    priceUuid: price.uuid,
                    type: 'inline'
                });
                return;
            }
            if (!isPercentageDeposit && price.price > 0 && price.price < depositAmount) {
                errors.push({
                    fieldName: 'pricing',
                    errorMessage: '',
                    key: ServiceCategoryType.PRICING,
                    petTypeId: price.petType!.id,
                    priceUuid: price.uuid,
                    type: 'alert'
                });
            }
        });
    }

    return errors;
};
